import * as React from 'react'
import type { CSSProperties } from 'react'
import { Check } from 'lucide-react'

import { cn } from '@/lib/utils'

/**
 * Checkbox component styled to match the design system.
 *
 * Uses native HTML checkbox for accessibility with custom styling.
 */

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  /** Label to display next to the checkbox. */
  label?: string
  /** Called when the checked state changes. */
  onCheckedChange?: (checked: boolean) => void
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, checked, onCheckedChange, id, style, ...props }, ref) => {
    const generatedId = React.useId()
    const inputId = id ?? generatedId

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onCheckedChange?.(e.target.checked)
    }

    return (
      <div style={styles.wrapper}>
        <div style={styles.checkboxContainer}>
          <input
            ref={ref}
            id={inputId}
            type="checkbox"
            checked={checked}
            onChange={handleChange}
            style={{ ...styles.input, ...style }}
            className={cn('checkbox', className)}
            {...props}
          />
          <Check
            style={{
              ...styles.checkIcon,
              opacity: checked ? 1 : 0,
            }}
          />
        </div>
        {label && (
          <label htmlFor={inputId} style={styles.label}>
            {label}
          </label>
        )}
      </div>
    )
  }
)
Checkbox.displayName = 'Checkbox'

/**
 * Toggle switch variant of checkbox.
 *
 * Same API as Checkbox but renders as a toggle switch.
 */
export interface ToggleProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  /** Label to display next to the toggle. */
  label?: string
  /** Called when the checked state changes. */
  onCheckedChange?: (checked: boolean) => void
}

export const Toggle = React.forwardRef<HTMLInputElement, ToggleProps>(
  ({ className, label, checked, onCheckedChange, id, disabled, ...props }, ref) => {
    const generatedId = React.useId()
    const inputId = id ?? generatedId

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onCheckedChange?.(e.target.checked)
    }

    return (
      <div style={styles.wrapper}>
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          onClick={() => !disabled && onCheckedChange?.(!checked)}
          style={{
            ...styles.toggleButton,
            background: checked ? 'var(--accent-primary)' : 'var(--bg-tertiary)',
            opacity: disabled ? 0.5 : 1,
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
          className={cn('toggle', className)}
          disabled={disabled}
        >
          <span
            style={{
              ...styles.toggleThumb,
              transform: checked ? 'translateX(16px)' : 'translateX(0)',
            }}
          />
        </button>
        {/* Hidden input for form submission */}
        <input
          ref={ref}
          id={inputId}
          type="checkbox"
          checked={checked}
          onChange={handleChange}
          style={styles.srOnly}
          {...props}
        />
        {label && (
          <label htmlFor={inputId} style={styles.label}>
            {label}
          </label>
        )}
      </div>
    )
  }
)
Toggle.displayName = 'Toggle'

const styles: Record<string, CSSProperties> = {
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  checkboxContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  input: {
    width: '16px',
    height: '16px',
    flexShrink: 0,
    appearance: 'none',
    borderRadius: '4px',
    border: '1px solid var(--border-default)',
    background: 'var(--bg-secondary)',
    transition: 'all var(--transition-fast)',
    cursor: 'pointer',
    outline: 'none',
  },
  checkIcon: {
    position: 'absolute',
    width: '12px',
    height: '12px',
    color: 'white',
    pointerEvents: 'none',
    transition: 'opacity var(--transition-fast)',
  },
  label: {
    fontSize: '14px',
    color: 'var(--text-primary)',
    cursor: 'pointer',
    userSelect: 'none',
  },
  toggleButton: {
    position: 'relative',
    display: 'inline-flex',
    height: '20px',
    width: '36px',
    flexShrink: 0,
    alignItems: 'center',
    borderRadius: '9999px',
    border: '2px solid transparent',
    transition: 'background var(--transition-fast)',
    outline: 'none',
  },
  toggleThumb: {
    pointerEvents: 'none',
    display: 'block',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    background: 'white',
    boxShadow: 'var(--shadow-sm)',
    transition: 'transform var(--transition-fast)',
  },
  srOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
}
